/**
 * Authentication Provider Service
 * Microservice-compatible authentication interface supporting multiple providers
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { TokenService, TokenStorage } from './token.service';
import type { User } from '../context/AuthContext';
import { AUTH_API_ENDPOINTS } from '../config/api.config';

export interface AuthProviderConfig {
  baseUrl: string;
  loginEndpoint?: string;
  refreshEndpoint?: string;
  logoutEndpoint?: string;
  introspectEndpoint?: string;
  timeout?: number;
  headers?: Record<string, string>;
}

export interface LoginCredentials {
  email: string;
  password: string;
  [key: string]: unknown;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number; // Token expiration time in seconds
  user: User;
  tokenType?: string; // Usually 'Bearer'
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
  tokenType?: string;
}

export interface TokenIntrospectionResponse {
  active: boolean;
  exp?: number;
  iat?: number;
  sub?: string;
  email?: string;
  name?: string;
  roles?: string[];
  [key: string]: unknown;
}

export interface ApiError {
  message: string;
  statusCode?: number;
  errors?: Record<string, string[]>;
}

/**
 * Abstract base class for authentication providers
 */
export abstract class AuthProvider {
  protected config: AuthProviderConfig;
  protected tokenService: TokenService;
  protected client: AxiosInstance;

  constructor(config: AuthProviderConfig, tokenService?: TokenService, tokenStorage?: TokenStorage) {
    this.config = {
      loginEndpoint: AUTH_API_ENDPOINTS.login,
      refreshEndpoint: AUTH_API_ENDPOINTS.refresh,
      logoutEndpoint: AUTH_API_ENDPOINTS.logout,
      introspectEndpoint: AUTH_API_ENDPOINTS.introspect,
      timeout: 30000,
      ...config,
    };

    this.tokenService = tokenService ?? new TokenService(tokenStorage);
    this.client = axios.create({
      baseURL: this.config.baseUrl,
      timeout: this.config.timeout,
      headers: {
        'Content-Type': 'application/json',
        ...this.config.headers,
      },
    });
  }

  /**
   * Login with credentials
   */
  abstract login(credentials: LoginCredentials): Promise<LoginResponse>;

  /**
   * Refresh access token
   */
  abstract refreshToken(refreshToken: string): Promise<RefreshTokenResponse>;

  /**
   * Logout (optional - may not be supported by all providers)
   */
  abstract logout(accessToken: string): Promise<void>;

  /**
   * Introspect token (validate token with auth service)
   */
  abstract introspectToken(token: string): Promise<TokenIntrospectionResponse>;

  /**
   * Get current access token from storage
   */
  async getAccessToken(): Promise<string | null> {
    return await this.tokenService.getAccessToken();
  }

  /**
   * Get current refresh token from storage
   */
  async getRefreshToken(): Promise<string | null> {
    return await this.tokenService.getRefreshToken();
  }

  /**
   * Check if current token is valid
   */
  async isTokenValid(): Promise<boolean> {
    const token = await this.getAccessToken();
    if (!token) {
      return false;
    }
    return await this.tokenService.hasValidToken();
  }
}

/**
 * Standard REST API Authentication Provider
 */
export class RestAuthProvider extends AuthProvider {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await this.client.post<LoginResponse>(
        this.config.loginEndpoint!,
        credentials
      );

      // Store tokens
      await this.tokenService.setAccessToken(response.data.accessToken);
      await this.tokenService.setRefreshToken(response.data.refreshToken);

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ApiError>;
      throw this.handleError(axiosError, 'Sign in failed');
    }
  }

  async refreshToken(refreshTokenValue: string): Promise<RefreshTokenResponse> {
    try {
      const response = await this.client.post<RefreshTokenResponse>(
        this.config.refreshEndpoint!,
        { refreshToken: refreshTokenValue }
      );

      // Update stored tokens
      await this.tokenService.setAccessToken(response.data.accessToken);
      if (response.data.refreshToken) {
        await this.tokenService.setRefreshToken(response.data.refreshToken);
      }

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ApiError>;
      throw this.handleError(axiosError, 'Token refresh failed');
    }
  }

  async logout(accessToken: string): Promise<void> {
    try {
      await this.client.post(
        this.config.logoutEndpoint!,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
    } catch (error) {
      // Log but don't throw - logout should always succeed locally
      console.error('Logout API call failed:', error);
    }
  }

  async introspectToken(token: string): Promise<TokenIntrospectionResponse> {
    try {
      const response = await this.client.post<TokenIntrospectionResponse>(
        this.config.introspectEndpoint!,
        { token }
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ApiError>;
      throw this.handleError(axiosError, 'Token introspection failed');
    }
  }

  protected handleError(error: AxiosError<ApiError>, defaultMessage: string): Error {
    if (error.response) {
      const apiError = error.response.data;
      return new Error(
        apiError?.message ||
        `${defaultMessage}: ${error.response.statusText}` ||
        defaultMessage
      );
    }

    if (error.request) {
      // Check for CORS errors specifically
      const isCorsError = 
        error.code === 'ERR_NETWORK' || 
        error.message?.includes('CORS') ||
        error.message?.includes('Network Error') ||
        (error.request.status === 0 && error.request.readyState === 4);

      if (isCorsError) {
        const isDevelopment = import.meta.env.DEV;
        const baseUrl = this.config.baseUrl;
        
        if (isDevelopment) {
          return new Error(
            `CORS Error: The API at ${baseUrl} is not allowing requests from this origin. ` +
            `In development, ensure VITE_AUTH_SERVICE_URL, VITE_API_USER_BASE_URL (User Service), and related API base URLs are set correctly and the Vite proxy is working. ` +
            `If the proxy is not working, check your vite.config.ts proxy configuration.`
          );
        } else {
          return new Error(
            `CORS Error: The API at ${baseUrl} is not configured to allow requests from this origin. ` +
            `Please contact your administrator to configure CORS headers on the API server. ` +
            `Required headers: Access-Control-Allow-Origin, Access-Control-Allow-Methods, Access-Control-Allow-Headers`
          );
        }
      }

      return new Error('Network error. Please check your connection and try again.');
    }

    return new Error(`An unexpected error occurred: ${defaultMessage}`);
  }
}

/**
 * OAuth2 Authentication Provider
 */
export class OAuth2AuthProvider extends RestAuthProvider {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    // OAuth2 typically uses different endpoints and grant types
    try {
      const response = await this.client.post<LoginResponse>(
        this.config.loginEndpoint!,
        {
          grant_type: 'password',
          username: credentials.email,
          ...credentials,
        }
      );

      await this.tokenService.setAccessToken(response.data.accessToken);
      await this.tokenService.setRefreshToken(response.data.refreshToken);

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ApiError>;
      throw this.handleError(axiosError, 'OAuth2 sign in failed');
    }
  }
}

/**
 * Factory function to create auth provider based on configuration
 * Note: MSAL provider requires PublicClientApplication instance and should be created separately
 */
export function createAuthProvider(
  config: AuthProviderConfig,
  providerType: 'rest' | 'oauth2' | 'msal' = 'rest',
  tokenService?: TokenService,
  tokenStorage?: TokenStorage
): AuthProvider {
  switch (providerType) {
    case 'oauth2':
      return new OAuth2AuthProvider(config, tokenService, tokenStorage);
    case 'msal':
      // MSAL provider requires PublicClientApplication instance
      // This should be created using createMsalAuthProvider from msalAuthProvider.service.ts
      throw new Error('MSAL provider must be created using createMsalAuthProvider. Use initializeMsalAuth instead.');
    case 'rest':
    default:
      return new RestAuthProvider(config, tokenService, tokenStorage);
  }
}

